import React, { Component } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import moment from 'moment';
import numeral from 'numeral';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
);

class Sparkline extends Component {
    constructor(props) {
        super(props);
        this.ref = React.createRef(null);
        this.setColor = this.setColor.bind(this);
        this.handleMoodeChange = this.handleMoodeChange.bind(this);

        const chartData = {
            labels: props.labels ?? props.dataL,
            datasets: [
                {
                    label: 'Sparkline',
                    data: props.dataL,
                    borderColor: '#33B1A5',
                    borderWidth: 2,
                    borderRadius: 4,
                    tension: 0.4,
                },
            ],
        };

        const isDark = JSON.parse(localStorage.getItem('dark-mode'));

        this.state = {
            isDark: !!isDark,
            chartData,
        };
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.isDark !== prevState.isDark) {
            console.log('Call here');
            this.setColor();
        }
    }

    componentDidMount() {
        setTimeout(() => {
            this.setColor();
        }, 500);
        document.addEventListener('changeMoodEvent', this.handleMoodeChange);
    }

    componentWillUnmount() {
        document.removeEventListener('changeMoodEvent', this.handleMoodeChange);
    }

    setColor() {
        if (this.ref.current) {
            const chartInstance = this.ref.current;
            const ctx = chartInstance.ctx;
            let gradient;
            if (this.state.isDark) {
                // Chart.js draws in CSS-pixel space, but ctx.canvas.height is the backing store
                // (devicePixelRatio times larger), so spanning that would cut the fade off partway.
                const height = chartInstance.chartArea?.bottom ?? chartInstance.height;
                gradient = ctx.createLinearGradient(0, 0, 0, height);
                // Shades of the line color (#33B1A5) fading out, so the fill reads as green
                // against the dark background instead of the previous neutral grays.
                gradient.addColorStop(0, 'rgba(51, 177, 165, 0.16)');
                gradient.addColorStop(1, 'rgba(51, 177, 165, 0)');
            } else {
                gradient = ctx.createLinearGradient(0, 0, 0, ctx.canvas.height);
                gradient.addColorStop(0, '#d2fffd');
                gradient.addColorStop(0.2, '#e7f9f9');
                gradient.addColorStop(0.3, '#fff');
            }
            this.setState({
                chartData: {
                    ...this.state.chartData,
                    datasets: this.state.chartData.datasets.map((item) => ({
                        ...item,
                        backgroundColor: gradient,
                        fill: true,
                    })),
                },
            });
        }
    }

    handleMoodeChange() {
        const isDark = JSON.parse(localStorage.getItem('dark-mode'));
        this.setState({ isDark });
    }

    render() {
        const options = {
            maintainAspectRatio: false,
            radius: 0,
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        title: (yDatapoint) => {
                            if (this.props.dontShowLable) return null;
                            const date = moment(yDatapoint[0]?.label).format('ll').split(',')[0];

                            const title = this.props.titleType
                                ? this.props.titleType(date, yDatapoint[0]?.label)
                                : `Week Start: ${date}`;

                            return title;
                        },
                        label: (yDatapoint) =>
                            `${this.props.keyName}: ${
                                this.props.customDataType
                                    ? this.props.customDataType(yDatapoint.raw)
                                    : numeral(yDatapoint.raw).format('0.0a')
                            }`,
                    },
                    displayColors: false,
                },
            },
            interaction: {
                mode: 'nearest',
                intersect: false,
            },
            elements: {
                point: {
                    radius: 0, // invisible point
                    hitRadius: 10, // increase clickable area
                    hoverRadius: 6, // increase area for hover effect
                },
            },
            devicePixelRatio: 3,
            scales: {
                x: {
                    ticks: {
                        display: false,
                    },
                    grid: {
                        drawBorder: false,
                        display: false,
                    },
                },
                y: {
                    type: 'logarithmic',
                    // Metrics where lower is better (e.g. GSC position) read better upside down:
                    // the best value sits at the top of the line.
                    reverse: !!this.props.reverseY,
                    ticks: {
                        display: false,
                        beginAtZero: true,
                    },
                    grid: {
                        drawBorder: false,
                        display: false,
                    },
                },
            },
        };

        return (
            <div style={{ height: '55px', position: 'relative', paddingRight: '20px' }}>
                <Line data={this.state.chartData} options={options} ref={this.ref} />
            </div>
        );
    }
}

export default Sparkline;
